/**
 * v1.8 Cluster B.1 (D-146) — „Workspace erstellen". Ein Nutzer kann mehrere
 * Workspaces (Mandanten) besitzen; der Ersteller wird owner. Der Backend-
 * Bootstrap (POST /api/workspaces) legt Root-Ordner, owner-Mitgliedschaft, die
 * Autogruppe „all", System-/KI-Ordner und die Core-Pakete an — wie bei der
 * Registrierung. Nach dem Anlegen wird direkt in den neuen Workspace gewechselt.
 */

import { useState } from "react";

import { ryfeApi } from "../api";
import { useRyfe } from "../RyfeContext";

import "./ryfe.scss";

import type { RyfeWorkspace } from "../api";

export const CreateWorkspaceDialog = ({
  onClose,
  onCreated,
}: {
  onClose: () => void;
  onCreated: (ws: RyfeWorkspace) => void;
}) => {
  const { refresh, setWorkspace } = useRyfe();
  const [name, setName] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  const submit = async () => {
    if (!name.trim() || busy) {
      return;
    }
    setBusy(true);
    setError("");
    try {
      const created = await ryfeApi.createWorkspace(name.trim());
      const ws: RyfeWorkspace = {
        id: created.id,
        name: name.trim(),
        slug: created.slug,
        role: created.role,
      };
      // In den neuen Workspace wechseln und die Workspace-Liste nachladen.
      setWorkspace(ws);
      await refresh();
      onCreated(ws);
    } catch (e) {
      setError(
        (e as { status?: number })?.status === 409
          ? "Dieser Workspace-Slug ist bereits vergeben."
          : "Anlegen fehlgeschlagen.",
      );
      setBusy(false);
    }
  };

  return (
    <div
      className="ryfe-modal"
      role="dialog"
      aria-modal="true"
      aria-label="Workspace erstellen"
      onClick={onClose}
    >
      <div className="ryfe-modal__card" onClick={(e) => e.stopPropagation()}>
        <div className="ryfe-panel__title" style={{ margin: 0 }}>
          Workspace erstellen
        </div>

        <div className="ryfe-field-label">Name</div>
        <input
          className="ryfe-field-input"
          value={name}
          autoFocus
          placeholder="z. B. Acme GmbH"
          data-testid="ws-name"
          onChange={(e) => setName(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter") {
              submit();
            }
          }}
        />

        <div className="ryfe-notice">
          Autogen beim Erstellen: eigener Root-Ordner, du als owner, die
          Autogruppe „all", System- und KI-Ordner sowie die Core-Pakete.
        </div>

        {error && <div className="ryfe-notice ryfe-notice--warn">{error}</div>}

        <div className="ryfe-modal__actions">
          <button className="ryfe-btn ryfe-btn--ghost" onClick={onClose}>
            Abbrechen
          </button>
          <span style={{ flex: 1 }} />
          <button
            className="ryfe-btn ryfe-btn--primary"
            disabled={!name.trim() || busy}
            data-testid="ws-create"
            onClick={submit}
          >
            Workspace erstellen
          </button>
        </div>
      </div>
    </div>
  );
};
